import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Check, Loader, Image, Film, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UrlMedia } from './types';

interface MediaItem {
  id: string;
  url: string;
  thumbnail_url?: string;
  filename: string;
  media_type: string;
  mime_type: string;
  file_size: number;
  created_at: string;
}

interface Props {
  setUrlMediaItems: React.Dispatch<React.SetStateAction<UrlMedia[]>>;
}

function formatFileSize(bytes: number): string {
  if (!bytes) { return '0 B'; }
  if (bytes < 1024) { return `${bytes} B`; }
  if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function MediaThumb({ item }: { item: MediaItem }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const thumb = item.thumbnail_url || item.url;
  const isVideo = item.media_type === 'video';
  const isDocument = item.media_type === 'document' || item.mime_type?.includes('pdf');

  if (isDocument || failed || !thumb) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-50 flex flex-col items-center justify-center gap-1">
        {isVideo ? (
          <Film className="w-6 h-6 text-purple-400" />
        ) : isDocument ? (
          <FileText className="w-6 h-6 text-blue-400" />
        ) : (
          <Image className="w-6 h-6 text-gray-300" />
        )}
        <span className="text-[9px] font-medium text-gray-400 uppercase tracking-wider">
          {item.mime_type?.split('/').pop()?.toUpperCase() || item.media_type}
        </span>
      </div>
    );
  }

  return (
    <>
      <img
        src={thumb}
        alt={item.filename}
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
      {isVideo && (
        <div className="absolute top-1.5 left-1.5 bg-black/70 backdrop-blur-sm rounded-md px-1.5 py-0.5 flex items-center gap-1">
          <Film className="w-3 h-3 text-white" />
          <span className="text-[9px] text-white font-semibold">{t('comp_video_badge', 'VIDEO')}</span>
        </div>
      )}
    </>
  );
}

export const MediaLibraryTab: React.FC<Props> = ({ setUrlMediaItems }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'image' | 'video'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['media-library-picker', search, filter],
    queryFn: async () => {
      const params: Record<string, string> = { limit: '50' };
      if (search) {
        params.search = search;
      }
      if (filter !== 'all') {
        params.media_type = filter;
      }
      const { default: axios } = await import('axios');
      const api = axios.create({ baseURL: '/api' });
      const token = localStorage.getItem('access_token');
      if (token) {
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
      }
      const resp = await api.get('/media', { params });
      return (resp.data as { media?: MediaItem[] }).media || [];
    },
    staleTime: 10000,
  });

  const items: MediaItem[] = Array.isArray(data) ? data : [];

  const toggleSelect = (item: MediaItem) => {
    const next = new Set(selected);
    if (next.has(item.id)) {
      next.delete(item.id);
    } else {
      next.add(item.id);
    }
    setSelected(next);
  };

  const handleAdd = () => {
    const selectedItems = items.filter((m) => selected.has(m.id));
    const newMedia: UrlMedia[] = selectedItems.map((m) => ({
      id: `lib-${m.id}`,
      url: m.url || m.thumbnail_url || '',
      filename: m.filename,
      contentType: m.mime_type,
      type: m.media_type === 'video' ? 'video' as const : 'image' as const,
      status: 'done' as const,
      progress: 100,
      mediaId: m.id,
    }));
    setUrlMediaItems((prev) => [...prev, ...newMedia]);
    setSelected(new Set());
  };

  return (
    <div className="h-full flex flex-col">
      {/* Search & Filter */}
      <div className="flex gap-2 mb-2 flex-shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('composer_search_library', 'Search your media...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          {(['all', 'image', 'video'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                filter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f === 'all' ? t('composer_filter_all', 'All') : f === 'image' ? t('composer_filter_images', 'Images') : t('composer_filter_videos', 'Videos')}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable list area — fills remaining space */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader className="w-6 h-6 animate-spin text-gray-300" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Image className="w-10 h-10 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">{t('composer_no_media', 'No media found in your library')}</p>
          </div>
        ) : (
          <div className="space-y-1 pr-1">
            {items.map((item) => {
              const isSelected = selected.has(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => toggleSelect(item)}
                  className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg border transition-all text-left group ${
                    isSelected
                      ? 'border-blue-400 bg-blue-50/60 ring-1 ring-blue-200'
                      : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/50'
                  }`}
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 relative border border-gray-100">
                    <MediaThumb item={item} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{item.filename}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] text-gray-400 font-medium uppercase">
                        {item.mime_type?.split('/').pop() || item.media_type}
                      </span>
                      <span className="text-gray-200">·</span>
                      <span className="text-[10px] text-gray-400">{formatFileSize(item.file_size)}</span>
                    </div>
                  </div>
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300 group-hover:border-gray-400'
                  }`}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Add button — pinned at bottom */}
      {selected.size > 0 && (
        <button
          onClick={handleAdd}
          className="w-full py-1.5 mt-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
        >
          {t('composer_add_selected', 'Add {{count}} selected', { count: selected.size })}
        </button>
      )}
    </div>
  );
};
